import React, { useEffect, useState, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { toggleLang, applyLang, getCurrentLang } from '../utils/i18n';
import useSession from '../admin/useSession';
import { clearSession } from '../admin/auth';

const NAV_LINKS = [
  { to: '/', label: 'Découvrir', i18n: 'nav_discover', end: true },
  { to: '/histoire', label: 'Histoire', i18n: 'nav_history' },
  { to: '/spiritualite', label: 'Spiritualité', i18n: 'nav_spirituality' },
  { to: '/tourisme', label: 'Tourisme', i18n: 'nav_tourism' },
  { to: '/talents', label: 'Talents', i18n: 'nav_talents' },
  { to: '/asc', label: 'ASC & Foot', i18n: 'nav_football' },
  { to: '/actualites', label: 'Actualités', i18n: 'nav_news' },
  { to: '/mairie', label: 'Mairie', i18n: 'nav_mairie' },
  { to: '/galerie', label: 'Galerie', i18n: 'nav_gallery' },
  { to: '/caisse', label: 'Caisse', i18n: 'nav_caisse' },
  { to: '/annuaire', label: 'Annuaire', i18n: 'nav_directory' },
];

function getInitials(user) {
  if (!user) return '?';
  const p = (user.prenom || '').trim();
  const n = (user.nom || '').trim();
  if (p && n) return (p[0] + n[0]).toUpperCase();
  if (p) return p.slice(0, 2).toUpperCase();
  return (user.email || '?')[0].toUpperCase();
}

const UserMenu = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`user-menu${open ? ' open' : ''}`} ref={ref}>
      <button
        className="user-pill"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="user-av">{getInitials(user)}</span>
        <span className="user-name">{user.prenom || user.email.split('@')[0]}</span>
        <span className="user-chev" aria-hidden="true">▾</span>
      </button>

      <div className="user-pop" role="menu">
        <div className="user-pop-head">
          <span className="user-av lg">{getInitials(user)}</span>
          <div className="user-pop-id">
            <b>{user.fullName || `${user.prenom} ${user.nom}`}</b>
            <small>{user.email}</small>
            <span className={`role-tag role-${user.role}`}>
              {user.role === 'admin' ? 'Admin' :
               user.role === 'moderateur' ? 'Modérateur' :
               user.role === 'contributeur' ? 'Contributeur' : 'Membre'}
            </span>
          </div>
        </div>
        <Link to="/profil" className="user-pop-item" onClick={() => setOpen(false)}>
          <span className="ic">◯</span> Mon profil
        </Link>
        {user.role === 'admin' && (
          <Link to="/admin/dashboard" className="user-pop-item" onClick={() => setOpen(false)}>
            <span className="ic">◔</span> Espace admin
          </Link>
        )}
        <button
          className="user-pop-item user-pop-out"
          onClick={() => { setOpen(false); onLogout(); }}
        >
          <span className="ic">⤴</span> Déconnexion
        </button>
      </div>
    </div>
  );
};

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const user = session?.user || null;

  useEffect(() => {
    const nav = document.getElementById('nav');
    if (!nav) return;
    const onScroll = () => nav.classList.toggle('scrolled', window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    window.toggleLang = toggleLang;
    applyLang(getCurrentLang());
  });

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const handleLogout = () => {
    clearSession();
    navigate('/');
  };

  return (
    <>
      <nav className={`nav${menuOpen ? ' menu-open' : ''}`} id="nav">
        <div className="nav-inner">
          <Link className="brand" to="/">
            <span className="brand-mark"></span>
            <span>Cambérène<small>SN · 221</small></span>
          </Link>
          <div className="nav-links">
            {NAV_LINKS.map(({ to, label, i18n, end }) => (
              <NavLink key={to} to={to} end={end} data-i18n={i18n}>
                {label}
              </NavLink>
            ))}
          </div>
          <div className="nav-cta">
            {user ? (
              <UserMenu user={user} onLogout={handleLogout} />
            ) : (
              <Link to="/connexion" className="auth-pill" data-i18n="nav_login">
                Connexion
              </Link>
            )}
            <button
              className="lang-pill lang-toggle"
              onClick={toggleLang}
              aria-label="Switch language"
            >
              EN
            </button>
            <button
              className={`menu-btn${menuOpen ? ' open' : ''}`}
              aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span></span><span></span><span></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Menu mobile plein écran */}
      <div
        id="mobile-menu"
        className={`mobile-menu${menuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!menuOpen}
      >
        <div className="mobile-menu-bg" onClick={() => setMenuOpen(false)} />
        <div className="mobile-menu-inner">
          <div className="mobile-menu-head">
            <span className="eyebrow">Menu</span>
            <button
              className="mobile-menu-close"
              onClick={() => setMenuOpen(false)}
              aria-label="Fermer"
            >
              ✕
            </button>
          </div>

          {user && (
            <div className="mobile-user">
              <span className="user-av lg">{getInitials(user)}</span>
              <div>
                <b>{user.fullName || `${user.prenom} ${user.nom}`}</b>
                <small>{user.email}</small>
              </div>
            </div>
          )}

          <nav className="mobile-menu-links">
            {NAV_LINKS.map(({ to, label, end }, i) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                style={{ transitionDelay: `${menuOpen ? 80 + i * 35 : 0}ms` }}
              >
                <span className="num">{String(i + 1).padStart(2, '0')}</span>
                <span className="lbl">{label}</span>
                <span className="arr">→</span>
              </NavLink>
            ))}
          </nav>
          <div className="mobile-menu-foot">
            {user ? (
              <>
                <Link to="/profil" className="btn btn-primary" onClick={() => setMenuOpen(false)}>
                  <span>Mon profil</span><span className="arrow">→</span>
                </Link>
                {user.role === 'admin' && (
                  <Link to="/admin/dashboard" className="btn btn-ghost" onClick={() => setMenuOpen(false)}>
                    <span>Espace admin</span><span className="arrow">↗</span>
                  </Link>
                )}
                <button
                  className="btn btn-ghost"
                  onClick={() => { setMenuOpen(false); handleLogout(); }}
                >
                  <span>Déconnexion</span><span className="arrow">⤴</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/connexion" className="btn btn-primary" onClick={() => setMenuOpen(false)}>
                  <span>Connexion</span><span className="arrow">→</span>
                </Link>
                <Link to="/inscription" className="btn btn-ghost" onClick={() => setMenuOpen(false)}>
                  <span>Créer un compte</span><span className="arrow">↗</span>
                </Link>
              </>
            )}
            <button className="lang-pill" onClick={toggleLang}>EN / FR</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
